import React from 'react';
import { TITLES, autoStatus } from '../../services/tripleCrown';
import { CAR_CLASSES, LAP_OPTIONS, TYRES, getVariablesByTitle } from '../../data/tracks';
import TrackMultiSelect from './TrackMultiSelect';
import TrackSettings from './TrackSettings';
import CarField from './CarField';

/* ══════════════════════════════════════
   ROUND EDITOR (one round/class)
   ══════════════════════════════════════ */
export default function RoundEditor({ round, index, onChange, onRemove, rules }) {
  const variables = getVariablesByTitle(round.titleId);
  const set = (k, v) => onChange(index, { ...round, [k]: v });

  const toggleVariable = (varId) => {
    const current = round.variables || [];
    if (current.includes(varId)) set('variables', current.filter(v => v !== varId));
    else set('variables', [...current, varId]);
  };

  const computedStatus = autoStatus(round.startDate, round.endDate);

  return (
    <div className="card" style={{ marginBottom: 14, borderLeft: '3px solid var(--teal)' }}>
      <div className="card-header">
        <h3 className="card-title" style={{ fontSize: 14 }}>
          Round {index + 1}
          {round.name ? ` — ${round.name}` : ''}
          <span className={`badge ${computedStatus === 'active' ? 'badge-success' : computedStatus === 'completed' ? 'badge-gold' : 'badge-muted'}`}
            style={{ marginLeft: 8, fontSize: 10 }}>{computedStatus}</span>
        </h3>
        <button className="btn btn-danger btn-sm" onClick={() => onRemove(index)}>Remove</button>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Game Title</label>
          <select className="form-select" value={round.titleId} onChange={e => {
            onChange(index, { ...round, titleId: e.target.value, tracks: [], variables: [] });
          }}>
            {TITLES.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Scoring Mode</label>
          <select className="form-select" value={round.scoringMode} onChange={e => set('scoringMode', e.target.value)}>
            <option value="time">Time-Based</option>
            <option value="position">Position-Based</option>
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Round Name</label>
          <input className="form-input" value={round.name || ''} onChange={e => set('name', e.target.value)}
            placeholder="e.g. Open Time Trial, Semi-Final" />
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">Description</label>
        <textarea className="form-input" rows={3} value={round.description || ''} onChange={e => set('description', e.target.value)}
          placeholder="Brief description of this round" style={{ resize: 'vertical' }} />
      </div>

      <div className="form-group">
        <label className="form-label">Attached Rule Set</label>
        <select className="form-select" value={round.ruleId || ''} onChange={e => set('ruleId', e.target.value || null)}>
          <option value="">None</option>
          {(rules || []).map(r => <option key={r.id} value={r.id}>{r.title}</option>)}
        </select>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Start Date & Time</label>
          <input className="form-input" type="datetime-local" value={round.startDate || ''}
            onChange={e => set('startDate', e.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label">End Date & Time</label>
          <input className="form-input" type="datetime-local" value={round.endDate || ''}
            onChange={e => set('endDate', e.target.value)} />
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">Tracks</label>
        <TrackMultiSelect
          titleId={round.titleId}
          selected={round.tracks || []}
          onChange={(tracks) => set('tracks', tracks)}
        />
        <TrackSettings titleId={round.titleId} tracks={round.tracks || []} settings={round.trackSettings}
          onChange={v => set('trackSettings', v)} defaultLaps={round.laps} defaultCarClass={round.carClass} defaultTyre={round.tyre} />
      </div>

      <CarField name={round.carName} image={round.carImage} uploadKey={`wiz-${index}`}
        onName={v => set('carName', v)} onImage={v => set('carImage', v)} />

      <div className="form-row">
        <div className="form-group" style={{ maxWidth: 120 }}>
          <label className="form-label">Laps</label>
          <select className="form-select" value={round.laps || ''} onChange={e => set('laps', e.target.value ? parseInt(e.target.value) : null)}>
            <option value="">N/A</option>
            {LAP_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        {round.titleId === 'granTurismo7' && (
          <div className="form-group" style={{ maxWidth: 160 }}>
            <label className="form-label">Car Class</label>
            <select className="form-select" value={round.carClass || ''} onChange={e => set('carClass', e.target.value || null)}>
              <option value="">Any / not set</option>
              {CAR_CLASSES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        )}
        {round.titleId === 'granTurismo7' && (
          <div className="form-group" style={{ maxWidth: 170 }}>
            <label className="form-label">Tyres</label>
            <select className="form-select" value={round.tyre || ''} onChange={e => set('tyre', e.target.value || null)}>
              <option value="">Any / not set</option>
              {TYRES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        )}
        <div className="form-group" style={{ flex: 1 }}>
          <label className="form-label">Race Variables</label>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {variables.map(v => (
              <label key={v.id} style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', fontSize: 13 }}>
                <input type="checkbox" checked={(round.variables || []).includes(v.id)}
                  onChange={() => toggleVariable(v.id)} />
                {v.label}
              </label>
            ))}
            {variables.length === 0 && (
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>No variables for this title</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
